import { Url } from '@rossum/api-client';
import { SchemaAction, SchemaSection } from '@rossum/api-client/schemas';
import { Tooltip } from '@rossum/ui/material';
import { FieldValues } from 'react-hook-form';
import { FormulaPreviewTile } from '../../formulas/FormulaPreview/FormulaPreviewTile';
import { useActionOptions } from '../ThenSection/useActionOptions';
import { RuleFormType } from '../types';

type TestSectionProps<T extends FieldValues & RuleFormType> = {
  schemaContent: SchemaSection[];
  queueUrls: Url[];
  schemaUrl: Url;
  currentRuleConcept: T;
};

const ARTIFICIAL_CONDITION_ID = 'condition';

const createArtificialRuleSection = (formula: string): SchemaSection => ({
  id: 'rule_section',
  category: 'section',
  label: 'rule section',
  children: [
    {
      id: ARTIFICIAL_CONDITION_ID,
      category: 'datapoint',
      label: 'Formula condition',
      type: 'string',
      formula,
      uiConfiguration: { type: 'formula', edit: 'disabled' },
    },
  ],
});

const createRenderAction =
  (options: ReturnType<typeof useActionOptions>) => (action: SchemaAction) => {
    const config = options.find(knownAction =>
      knownAction.resolveAction(action)
    );
    const Icon = config?.icon;

    return Icon ? (
      <Tooltip title={config.name}>
        <Icon />
      </Tooltip>
    ) : null;
  };

export const TestSection = <T extends FieldValues & RuleFormType>({
  schemaContent,
  queueUrls,
  schemaUrl,
  currentRuleConcept,
}: TestSectionProps<T>) => {
  const options = useActionOptions();

  return (
    <FormulaPreviewTile
      type="rule"
      currentFormulaId={ARTIFICIAL_CONDITION_ID}
      renderAction={createRenderAction(options)}
      schemaContentDraft={[
        // Add artificial condition to the schema content
        createArtificialRuleSection(currentRuleConcept.triggerCondition),
        ...schemaContent,
      ]}
      schemaRulesDraft={[
        {
          ...currentRuleConcept,
          // Append current schema URL to make it work when adding new rule
          schema: schemaUrl,
          // Pretend that the rule is enabled for the sake of preview
          enabled: true,
        },
      ]}
      queueUrls={queueUrls}
    />
  );
};
